<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PipelinesAPI from 'dashboard/api/crm/pipelines';
import StagesAPI from 'dashboard/api/crm/stages';
import CardsAPI from 'dashboard/api/crm/cards';
import KanbanBoard from '../components/KanbanBoard.vue';
import PipelineSelector from '../components/PipelineSelector.vue';
import CardFormModal from '../components/CardFormModal.vue';
import StageFormModal from '../components/StageFormModal.vue';
import StageDeleteModal from '../components/StageDeleteModal.vue';

const route = useRoute();
const router = useRouter();

const pipelines = ref([]);
const stages = ref([]);
const cards = ref([]);
const activePipelineId = ref(null);
const loading = ref(false);
const error = ref(null);

const activePipeline = computed(() =>
  pipelines.value.find(p => p.id === activePipelineId.value)
);

async function loadPipelines() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = (data.payload || []).filter(p => !p.archived);
    const fromRoute = parseInt(route.params.pipelineId, 10);
    activePipelineId.value =
      pipelines.value.find(p => p.id === fromRoute)?.id ||
      pipelines.value[0]?.id ||
      null;
  } catch (e) {
    error.value = e?.response?.data?.message || e.message;
  } finally {
    loading.value = false;
  }
}

async function loadPipelineDetail(pipelineId) {
  if (!pipelineId) {
    stages.value = [];
    cards.value = [];
    return;
  }
  loading.value = true;
  try {
    const [{ data: pipelineData }, { data: cardsData }] = await Promise.all([
      PipelinesAPI.show(pipelineId),
      CardsAPI.listByPipeline(pipelineId),
    ]);
    stages.value = pipelineData.stages || [];
    cards.value = cardsData.payload || [];
  } catch (e) {
    error.value = e?.response?.data?.message || e.message;
  } finally {
    loading.value = false;
  }
}

watch(activePipelineId, async newId => {
  await loadPipelineDetail(newId);
  if (newId && route.params.pipelineId !== String(newId)) {
    router.replace({
      name: 'crm_pipeline',
      params: { accountId: route.params.accountId, pipelineId: newId },
    });
  }
});

async function handleCardMove({ cardId, stageId, position }) {
  const card = cards.value.find(c => c.id === cardId);
  if (!card) return;
  const previousStageId = card.stage_id;
  card.stage_id = stageId;
  card.position = position;
  try {
    await CardsAPI.move(cardId, { stageId, position });
  } catch (e) {
    card.stage_id = previousStageId;
    error.value = e?.response?.data?.message || 'Falha ao mover card';
  }
}

async function handleCreatePipeline(name) {
  const { data } = await PipelinesAPI.create({ pipeline: { name } });
  pipelines.value.push(data);
  activePipelineId.value = data.id;
  // Auto-seed (backend after_create_commit) populates 5 default stages —
  // re-fetch to render them.
  await loadPipelineDetail(data.id);
}

async function handleAddStage({ name, position }) {
  if (!activePipelineId.value) return;
  try {
    const { data } = await StagesAPI.create(activePipelineId.value, {
      name,
      position,
    });
    stages.value.push(data);
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao criar etapa';
  }
}

// ===== Stage edit / delete (Feature 2) =====
const showStageModal = ref(false);
const editingStage = ref(null);
const showStageDeleteModal = ref(false);
const stageBeingDeleted = ref(null);
const stageBeingDeletedActiveCount = ref(0);

function openEditStage(stage) {
  editingStage.value = stage;
  showStageModal.value = true;
}

async function handleStageSubmit(payload) {
  if (!activePipelineId.value) return;
  try {
    if (editingStage.value?.id) {
      const { data } = await StagesAPI.update(
        activePipelineId.value,
        editingStage.value.id,
        payload
      );
      const idx = stages.value.findIndex(s => s.id === data.id);
      if (idx >= 0) stages.value.splice(idx, 1, data);
    } else {
      const { data } = await StagesAPI.create(activePipelineId.value, payload);
      stages.value.push(data);
    }
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao salvar etapa';
  } finally {
    showStageModal.value = false;
    editingStage.value = null;
  }
}

function openDeleteStage(stage) {
  stageBeingDeleted.value = stage;
  stageBeingDeletedActiveCount.value = cards.value.filter(
    c => c.stage_id === stage.id && !c.archived
  ).length;
  showStageDeleteModal.value = true;
  // If the edit modal was open (delete clicked from inside it), close it.
  showStageModal.value = false;
}

async function handleStageDeleteConfirm({ targetStageId }) {
  const stage = stageBeingDeleted.value;
  if (!stage || !activePipelineId.value) return;
  try {
    // Move active cards out first if needed.
    if (stageBeingDeletedActiveCount.value > 0 && targetStageId) {
      await StagesAPI.moveCards(activePipelineId.value, stage.id, targetStageId);
      // Reflect move locally: change stage_id on every active card.
      cards.value = cards.value.map(c =>
        c.stage_id === stage.id && !c.archived
          ? { ...c, stage_id: targetStageId }
          : c
      );
    }
    await StagesAPI.delete(activePipelineId.value, stage.id);
    stages.value = stages.value.filter(s => s.id !== stage.id);
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao excluir etapa';
  } finally {
    showStageDeleteModal.value = false;
    stageBeingDeleted.value = null;
    stageBeingDeletedActiveCount.value = 0;
  }
}

// ===== Card create / edit / delete =====
const showCardModal = ref(false);
const editingCard = ref(null);
const modalStageId = ref(null);

function openCreateCard(stageId) {
  editingCard.value = null;
  modalStageId.value = stageId;
  showCardModal.value = true;
}

function openEditCard(card) {
  editingCard.value = card;
  modalStageId.value = card.stage_id;
  showCardModal.value = true;
}

async function handleCardSubmit(payload) {
  try {
    if (editingCard.value?.id) {
      const { data } = await CardsAPI.update(editingCard.value.id, { card: payload });
      const idx = cards.value.findIndex(c => c.id === data.id);
      if (idx >= 0) cards.value.splice(idx, 1, data);
      else cards.value.push(data);
    } else {
      const { data } = await CardsAPI.create({ card: payload });
      cards.value.push(data);
    }
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao salvar card';
  } finally {
    // Adversarial fix: always close modal on submit attempt to avoid stuck UI.
    // Errors are surfaced via inline error banner; user can retry by re-opening.
    showCardModal.value = false;
    editingCard.value = null;
  }
}

async function handleCardDelete(cardId) {
  if (!cardId) return;
  try {
    await CardsAPI.delete(cardId);
    cards.value = cards.value.filter(c => c.id !== cardId);
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao excluir card';
  } finally {
    showCardModal.value = false;
    editingCard.value = null;
  }
}

onMounted(loadPipelines);
</script>

<template>
  <div class="flex flex-col h-full p-4 gap-4 bg-n-background overflow-hidden">
    <header class="flex items-center justify-between gap-3 flex-shrink-0">
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-semibold text-n-slate-12">CRM</h1>
        <PipelineSelector
          v-if="pipelines.length"
          :pipelines="pipelines"
          :active-pipeline-id="activePipelineId"
          @select="activePipelineId = $event"
          @create="handleCreatePipeline"
        />
      </div>
      <div v-if="loading" class="text-sm text-n-slate-10">Carregando…</div>
    </header>

    <div v-if="error" class="px-3 py-2 rounded bg-red-50 text-red-700 text-sm">
      {{ error }}
    </div>

    <KanbanBoard
      v-if="activePipeline"
      :stages="stages"
      :cards="cards"
      @move="handleCardMove"
      @add-stage="handleAddStage"
      @add-card="openCreateCard"
      @card-click="openEditCard"
      @edit-stage="openEditStage"
      @delete-stage="openDeleteStage"
    />

    <div
      v-else-if="!loading && pipelines.length === 0"
      class="flex-1 flex items-center justify-center text-n-slate-10"
    >
      <div class="text-center">
        <p class="mb-2">Nenhum pipeline criado.</p>
        <button
          class="px-4 py-2 rounded bg-n-brand text-white text-sm"
          @click="handleCreatePipeline('Comercial')"
        >
          Criar primeiro pipeline
        </button>
      </div>
    </div>

    <CardFormModal
      :show="showCardModal"
      :initial-card="editingCard || {}"
      :stage-id="modalStageId"
      @close="showCardModal = false; editingCard = null"
      @submit="handleCardSubmit"
      @delete="handleCardDelete"
    />

    <StageFormModal
      :show="showStageModal"
      :stage="editingStage || {}"
      @close="showStageModal = false; editingStage = null"
      @submit="handleStageSubmit"
      @delete="openDeleteStage"
    />

    <StageDeleteModal
      :show="showStageDeleteModal"
      :stage="stageBeingDeleted || {}"
      :pipeline-stages="stages"
      :active-cards-count="stageBeingDeletedActiveCount"
      @close="showStageDeleteModal = false; stageBeingDeleted = null"
      @confirm="handleStageDeleteConfirm"
    />
  </div>
</template>
